import { get, isNumber, isString } from "radash"

import { Trans } from "@be-miniapp/i18n"
import type { APIError } from "@be-miniapp/types"

import { formatCurrency } from "./number"
import { fromAPIErrorToToastMessage } from "./toast-error"

export interface RideBookingErrorHandlerParams {
  error: unknown
  t: (key: string, options?: unknown) => string
  modal: {
    show: (params: Record<string, unknown>) => void
    close: () => void
  }
  navigate: (to: string, options?: Record<string, unknown>) => void
  refetch?: () => void
  onPromoNotUsable?: (message?: string) => void
  dismiss?: () => void
}

export function handleRideBookingError({
  error,
  t,
  modal,
  navigate,
  refetch,
  onPromoNotUsable,
  dismiss,
}: RideBookingErrorHandlerParams) {
  const err = error as Record<string, unknown> | undefined

  if (err?.flag === 182) {
    refetch?.()
    modal.show({
      title: err?.message_title,
      message: err?.message,
      onPrimaryAction: () => {
        modal.close()
      },
      primaryLabel: t("common.got_it"),
    })
    return
  }

  if (err?.flag === 180) {
    onPromoNotUsable?.(err?.message as string | undefined)
    return
  }

  if (err?.flag === 755) {
    modal.show({
      iconType: "warning",
      title: t("ride_payment_failed"),
      message: (
        <Trans i18nKey="ride_payment_failed_desc">
          Bạn chưa thanh toán số tiền{" "}
          <strong>{formatCurrency(get(err, "user_debt", 0), "vi")}</strong> cho
          dịch vụ đã sử dụng trước đó. Vui lòng dùng ứng dụng be để thanh toán
          để tiếp tục sử dụng dịch vụ.
        </Trans>
      ),
      onPrimaryAction: () => {
        modal.close()
      },
      primaryLabel: t("common.got_it"),
    })
    return
  }

  if (err?.flag === 10002) {
    modal.show({
      title: err?.message_title,
      message: err?.message,
      onPrimaryAction: () => {
        modal.close()
      },
      primaryLabel: t("common.got_it"),
    })
    return
  }

  if (isNumber(err?.session_id) || isString(err?.session_id)) {
    modal.show({
      title: t("transport_having_another_trip_title"),
      message: t("transport_having_another_trip_desc", {
        addr:
          (err?.drop_location_address_name as string | undefined) ||
          String(err?.drop_location_address ?? "").split(",")[0],
      }),
      onPrimaryAction: () => {
        navigate(`/ride/intrip?session_id=${String(err?.session_id)}`)
        modal.close()
      },
      primaryLabel: t("transport_having_another_trip_cta_1"),
      onSecondaryAction: () => {
        modal.close()
        navigate("/ride/home", {
          replace: true,
        })
      },
      secondaryLabel: t("transport_having_another_trip_cta_2"),
      buttonOrientation: "vertical",
    })
    return
  }

  dismiss?.()
  const messageData = fromAPIErrorToToastMessage(error as APIError)
  modal.show({
    message: messageData.description,
    onPrimaryAction: () => {
      modal.close()
    },
    primaryLabel: t("common.ok"),
  })
}
